/* cspell:ignore nics */
import type {
  NicConfigurationItem,
  NicConfigurationValue,
  NicResourceConfiguration,
  NicResourceQuantity,
  NicVendorDeviceSelection,
} from '@/types/nic-configuration';

/** 读取网卡分类字段；兼容尚未提供 deviceType 的旧数据。 */
export function getNicDeviceType(nic: Pick<NicResourceConfiguration, 'deviceType' | 'resourceType'>): string {
  return String(nic.deviceType || nic.resourceType || '').trim();
}

/** 判断资源配置是否属于当前计费选中的厂商设备。 */
export function isNicVendorDeviceMatch(
  configuration: Pick<NicResourceConfiguration, 'vendor' | 'vendorID' | 'device' | 'deviceID'>,
  selection: NicVendorDeviceSelection,
): boolean {
  const normalize = (value?: string) =>
    String(value || '')
      .trim()
      .toLowerCase();
  const vendorMatched =
    selection.vendorID && configuration.vendorID
      ? normalize(configuration.vendorID) === normalize(selection.vendorID)
      : normalize(configuration.vendor) === normalize(selection.vendor);
  const deviceMatched =
    selection.deviceID && configuration.deviceID
      ? normalize(configuration.deviceID) === normalize(selection.deviceID)
      : normalize(configuration.device) === normalize(selection.device);
  return vendorMatched && deviceMatched;
}

/** 网卡配置唯一标识：同类型下允许存在多个厂商设备。 */
export function getNicConfigurationKey(
  nic: Pick<NicResourceConfiguration, 'deviceType' | 'resourceType' | 'vendorID' | 'deviceID' | 'vendor' | 'device'>,
): string {
  return [getNicDeviceType(nic), nic.vendorID || nic.vendor, nic.deviceID || nic.device, nic.resourceType].join('::');
}

/** 将接口返回的网卡类型转换为可逐网段勾选的提交结构。 */
export function createNicConfigurationItem(configuration: NicResourceConfiguration): NicConfigurationItem {
  return {
    deviceType: configuration.deviceType,
    vendor: configuration.vendor,
    vendorID: configuration.vendorID,
    device: configuration.device,
    deviceID: configuration.deviceID,
    resourceType: configuration.resourceType,
    linkSpeed: configuration.nic?.linkSpeed,
    linkType: configuration.nic?.linkType,
    resource: {},
  };
}

/** 更新某块网卡选中的网段；未选网段的物理网卡不进入提交资源。 */
export function setNicConfigurationCidrBlocks(
  nic: NicConfigurationItem,
  quantity: NicResourceQuantity,
  cidrBlocks: string[],
): NicConfigurationItem {
  const resource = { ...nic.resource };
  if (cidrBlocks.length) {
    resource[quantity.resourceName] = {
      quantity: { limit: '1', request: '1' },
      networkAttachmentDefine: quantity.networkAttachmentDefinition,
      cidrBlocks,
    };
  } else {
    delete resource[quantity.resourceName];
  }
  return { ...nic, resource };
}

/** 已选的每种网卡类型都必须至少勾选一个网段。 */
export function isNicConfigurationComplete(nics: NicConfigurationValue | undefined): boolean {
  return !!nics?.length && nics.every((nic) => Object.values(nic.resource).some((item) => item.cidrBlocks.length > 0));
}

/** max 存在且不为 0 时，对应网段才允许选择。 */
export function isNicQuantityAvailable(quantity: NicResourceQuantity): boolean {
  return typeof quantity.max === 'number' && quantity.max !== 0;
}

/** 汇总网卡已选资源中的 IPv4 网段，并移除重复项。 */
export function getNicConfigurationCidrBlocks(nic: NicConfigurationItem): string[] {
  return [...new Set(Object.values(nic.resource).flatMap((resource) => resource.cidrBlocks.filter((cidr) => !cidr.includes(':'))))];
}

/** 网卡摘要单行：deviceType · cidrBlocks */
export function formatNicSummaryValue(nic: NicConfigurationItem): string {
  const deviceType = getNicDeviceType(nic);
  const cidrs = getNicConfigurationCidrBlocks(nic).join(', ');
  return cidrs ? `${deviceType} · ${cidrs}` : deviceType;
}

/** 网卡厂商设备摘要（与 NicConfiguration hardwareSummary.deviceSummary 一致） */
export function formatNicHardwareSummary(nic: { vendor?: string; device?: string }): string {
  return [nic.vendor, nic.device].filter(Boolean).join(' · ') || '-';
}

/** 多块网卡厂商设备摘要（去重后用分号连接） */
export function buildNicVendorDeviceSummary(nics: NicConfigurationValue | undefined): string {
  if (!nics?.length) return '-';
  const labels = [...new Set(nics.map(formatNicHardwareSummary).filter((item) => item !== '-'))];
  return labels.length ? labels.join('; ') : '-';
}

/** 多块网卡摘要行（卡片 / 只读预览复用） */
export function buildNicSummaryLines(nics: NicConfigurationValue | undefined): string[] {
  if (!nics?.length) return [];
  return nics.map(formatNicSummaryValue);
}
